/**
 * A SCREEN'S FIRST OPEN (the loading rule, 1): the full ring, centred, until the screen's first fetches
 * are all in (`useFirstLoad`), a short resolve (`useLoaderPhase`, `RESOLVE_MS`), then the screen whole.
 * Mounted once, by the shell, around whichever screen is open, so every screen keeps the rule without
 * a line of its own: the screen mounts at once, hidden and inert, so every one of its fetches starts,
 * and it is shown when the last first fetch is in. On the reader's side there are never grey blocks on
 * a first open, and never cards assembling one by one.
 *
 * ONCE A SCREEN. A screen whose content has been shown in this visit (`OPENED`) opens straight to it;
 * what it then reloads shows its own placeholders (`useDelayedPending`, `CardPlaceholder`). `screen`
 * is the key: the shell keys the Overview and a site's hub by the site, so another site is another
 * first open, as the Overview's loader always was.
 *
 * ITS ESSENTIALS, WHERE IT NAMES THEM. A screen that knows the data its first paint needs says so
 * (`useScreenReady`), and is shown as soon as that is in; its slower cards arrive behind their own
 * placeholders. A screen that says nothing waits for every first fetch, capped (`first-load.ts`).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useFirstLoad, useLoaderPhase } from './first-load';
import { OPENED, gateReady } from './gate';
import { ScreenReadyContext } from './screen-ready';
import { RingLoader } from './RingLoader';
import { RESOLVE_MS } from './scene';

export function ScreenGate({
  screen,
  label,
  steps = false,
  children,
}: {
  /** The screen's key: a first open is a key not yet shown in this visit. */
  screen: string;
  /** What the line under the ring says is being read ("Reading Alarms…"). */
  label: string;
  /** Name the piece of the plant being read beside the line (a site's Overview). */
  steps?: boolean;
  children: ReactNode;
}): ReactNode {
  const key = OPENED.has(screen) ? undefined : screen;
  const firstLoaded = useFirstLoad(key);
  /* What the screen has said of its essentials, for the screen it said it of. */
  const [declared, setDeclared] = useState<{ screen: string; ready: boolean } | null>(null);
  const declare = useCallback((ready: boolean) => setDeclared({ screen, ready }), [screen]);
  const ready = gateReady({ firstLoaded, declared: declared?.screen === screen ? declared.ready : null });
  const phase = useLoaderPhase(ready, key, RESOLVE_MS);

  useEffect(() => {
    if (key !== undefined && phase.cards) OPENED.mark(key);
  }, [key, phase.cards]);

  /* Once shown, the gate's two wrappers lay out as nothing (`contents`), so a screen that fills the
     view (the twin, the studio) keeps its own height. */
  return (
    <div className={phase.loader ? 'relative' : 'contents'}>
      {phase.loader && (
        <div className="lm-gate-ring">
          <RingLoader label={label} done={phase.resolving} steps={steps} />
        </div>
      )}
      <div className={phase.cards ? 'contents' : 'invisible'} aria-hidden={!phase.cards} inert={!phase.cards}>
        <ScreenReadyContext.Provider value={declare}>{children}</ScreenReadyContext.Provider>
      </div>
    </div>
  );
}

/** The ring alone, where a screen's code is still arriving (the shell's `Suspense`). */
export function ScreenRing({ label, steps = false }: { label: string; steps?: boolean }): ReactNode {
  return (
    <div className="relative">
      <div className="lm-gate-ring">
        <RingLoader label={label} steps={steps} />
      </div>
    </div>
  );
}
